import * as React from "react";
import {
  DateCalendar,
  DatePicker as DatePickerField,
  DatePickerProps,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useTheme } from "@mui/material";
import { Control, Controller } from "react-hook-form";
import dayjs from "dayjs";
import { tokens } from "../../theme";

export type DatePickerFieldProps = {
  color?: "primary" | "secondary" | "success" | "error" | "info" | "warning";
  size?: "small" | "medium";
  label?: string | "";
  variant?: "filled" | "outlined" | "standard" | undefined;
  fullWidth?: true | false;
  name?: string;
  control: Control<any, any>;
  controllerName?: string;
  onChange?: (date: Date | null) => void;
  rules?: object;
  disabled?: boolean;
  showErrorMinLength?: number;
  isDisablePast?: boolean;
  isDisableFuture?: boolean;
  maxDate?: Date;
  minDate?: Date;
  onBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  isDateCalendar?: boolean;
  isMobileView?: boolean;
  popperZIndex?: number;
};

const CustomDatePicker: React.FC<
  DatePickerFieldProps & DatePickerProps<Date>
> = ({
  size,
  color,
  label,
  variant,
  fullWidth,
  isDisablePast,
  isDisableFuture,
  name,
  control,
  controllerName,
  onChange,
  rules,
  disabled,
  showErrorMinLength,
  maxDate,
  minDate,
  onBlur,
  isDateCalendar = true,
  isMobileView = false,
  popperZIndex,
  // ── extract so it doesn't land in ...props and silently override internals ──
  slotProps: externalSlotProps,
  ...props
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const isRequired = () =>
    !!(rules && typeof rules === "object" && "required" in rules && label);

  const getLabel = () => {
    if (isRequired()) {
      return (
        <span>
          {label}{" "}
          <span style={{ color: colors.success[200], fontSize: "18px" }}>*</span>
        </span>
      );
    }
    return label;
  };

  const parseDate = (e: any, fieldOnChange: any) => {
    let dateStr: string = e.target.value;
    const lastDigit: string = dateStr.substring(9, 10);
    if (isNaN(Number(lastDigit))) return;

    if (dateStr.length === 10) {
      const month = dateStr.substring(0, 2);
      const day = dateStr.substring(3, 5);
      if (dateStr.substring(6, 8) === "00") {
        const currentCentury = Math.floor(dayjs().year() / 100);
        const expectedCentury =
          Number(dateStr.substring(8, 10)) > 50
            ? currentCentury - 1
            : currentCentury;
        const year = expectedCentury + dateStr.substring(8, 10);
        dateStr = `${month}/${day}/${year}`;
      }
    }

    fieldOnChange({ ...e, target: { ...e.target, value: dateStr } });
  };

  return (
    <Controller
      name={controllerName || ""}
      control={control}
      rules={rules ?? {}}
      render={({ field: { ref, ...field }, fieldState }) => (
        <>
          {isDateCalendar ? (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePickerField
                {...props}
                {...field}
                orientation="portrait"
                name={name ?? controllerName}
                onChange={(e) => {
                  field.onChange(e);
                  onChange?.(e);
                }}
                label={getLabel() || ""}
                disablePast={isDisablePast}
                disableFuture={isDisableFuture}
                // @ts-ignore
                // maxDate={maxDate}   // CS-1984: wrap raw Date in dayjs for type-correctness
                maxDate={maxDate ? dayjs(maxDate) : undefined}   // CS-1984
                // @ts-ignore
                // minDate={minDate}   // CS-1984: wrap raw Date in dayjs for type-correctness
                minDate={minDate ? dayjs(minDate) : undefined}   // CS-1984
                // @ts-ignore
                value={field.value ? dayjs(field.value) : null}
                disabled={disabled}
                desktopModeMediaQuery={isMobileView ? undefined : "(min-width: 0px)"}
                slotProps={{
                  // deep-merge so callers can still extend without clobbering size/color/etc.
                  ...externalSlotProps,
                  ...(popperZIndex !== undefined
                    ? {
                        popper: {
                          placement: "bottom-start",
                          ...(externalSlotProps as any)?.popper,
                          sx: {
                            ...((externalSlotProps as any)?.popper?.sx ?? {}),
                            zIndex: popperZIndex,
                          },
                          modifiers: [
                            { name: "flip", enabled: false },
                            ...((externalSlotProps as any)?.popper?.modifiers ?? []),
                          ],
                        },
                      }
                    : {}),
                  textField: {
                    // caller's textField props come first (lower priority)
                    ...(externalSlotProps as any)?.textField,
                    // internal props always win
                    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                      parseDate(e, field.onChange);
                      onBlur?.(e);
                      (externalSlotProps as any)?.textField?.onBlur?.(e);
                    },
                    // ── THE FIX: size is now explicitly set from the prop ──
                    size: size ?? "medium",
                    color:
                      color ??
                      (theme.palette.mode === "dark" ? "secondary" : "primary"),
                    fullWidth: fullWidth !== undefined ? fullWidth : true,
                    error: !!fieldState.error,
                    helperText: fieldState.error
                      ? showErrorMinLength &&
                        showErrorMinLength <=
                          (fieldState.error.message?.length ?? 0)
                        ? null
                        : fieldState.error.message
                      : null,
                    sx: {
                      // caller's sx merged under internal sx
                      ...((externalSlotProps as any)?.textField?.sx ?? {}),
                      ...(fieldState.error
                        ? {
                            "& .MuiInputBase-input": { color: theme.palette.error.main },
                            "& .MuiFormLabel-root": { color: theme.palette.error.main },
                            "& .MuiInput-underline:after": { borderBottomColor: theme.palette.error.dark },
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": { borderColor: theme.palette.error.main },
                              "&:hover fieldset": { borderColor: theme.palette.error.dark },
                              "&.Mui-focused fieldset": { borderColor: theme.palette.error.dark },
                              "&.Mui-focused .MuiFormLabel-root": { color: theme.palette.error.dark },
                            },
                          }
                        : {
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor:
                                  theme.palette.mode === "dark" ? "" : colors.primary[100],
                              },
                            },
                            "& .MuiInputLabel-outlined": {
                              color:
                                theme.palette.mode === "dark" ? "" : colors.primary[100],
                            },
                          }),
                    },
                  },
                }}
              />
            </LocalizationProvider>
          ) : (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                {...field}
                value={field.value ? dayjs(field.value) : null}
                onChange={(date) => {
                  field.onChange(date);
                  onChange?.(date?.toDate() ?? null);
                }}
                disablePast={isDisablePast}
                disableFuture={isDisableFuture}
                maxDate={maxDate ? dayjs(maxDate) : undefined}
                minDate={minDate ? dayjs(minDate) : undefined}
                sx={{
                  borderRadius: "8px",
                  padding: "8px",
                  boxShadow: theme.shadows[theme.palette.mode === "dark" ? 12 : 4],
                  /* Dark theme uses primary.main = grey[900], so selected day was nearly invisible on dark UI */
                  "& .MuiPickersDay-root.Mui-selected": {
                    backgroundColor: "#14b8a6 !important",
                    color: "#ffffff !important",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#0d9488 !important",
                    },
                    "&:focus": {
                      backgroundColor: "#14b8a6 !important",
                    },
                  },
                  "& .MuiPickersDay-root.Mui-selected.MuiPickersDay-today": {
                    borderColor: "#14b8a6",
                    color: "#ffffff !important",
                  },
                  "& .MuiDayCalendar-weekContainer .MuiPickersDay-root:focus.Mui-selected": {
                    backgroundColor: "#14b8a6 !important",
                  },
                }}
              />
            </LocalizationProvider>
          )}
        </>
      )}
    />
  );
};

export default CustomDatePicker;